const zone = new Intl.DateTimeFormat('sv-SE', {timeZone:'Europe/Rome',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
const cache = new Map();
const valid = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) > -99.9 && Number(value) < 70;
// Archive timestamps without an offset use the API's Europe/Rome clock.
export function romeClock(value) {
    if (!value) return null;
    const raw = String(value);
    if (/^\d{4}-\d\d-\d\d[T ]\d\d:\d\d(?::\d\d(?:\.\d+)?)?$/.test(raw)) return raw.replace(' ', 'T').slice(0,19).padEnd(19, ':00');
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return null;
    const parts = Object.fromEntries(zone.formatToParts(date).map(p => [p.type,p.value]));
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}
function civilTime(clock) { return Date.parse(`${clock}Z`); }
export function previousDay(day) { return new Date(Date.parse(`${day}T12:00:00Z`) - 86400000).toISOString().slice(0,10); }
export function deltaText(value) {
    const rounded = Math.round(value * 10) / 10;
    return `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${Math.abs(rounded).toLocaleString('it-IT',{minimumFractionDigits:1,maximumFractionDigits:1})} °C`;
}
export function temperatureComparisons(station, rows, daily, now = new Date()) {
    const clock = romeClock(station.time_utc ?? station.last_update ?? station.updated_at ?? station.ora ?? station.data);
    const currentClock = romeClock(now.toISOString());
    if (!clock || Number(station.is_stale) === 1 || Math.abs(civilTime(currentClock) - civilTime(clock)) > 2 * 3600000) return {live:null,max:null,min:null};
    const day = previousDay(clock.slice(0,10));
    const target = civilTime(`${day}${clock.slice(10)}`);
    const points = (Array.isArray(rows) ? rows : []).map(row => ({clock:romeClock(row.observed_at ?? row.data_rilevazione ?? row.timestamp),value:row.temperature ?? row.temperatura}))
        .filter(row => row.clock?.startsWith(day) && valid(row.value)).map(row => ({time:civilTime(row.clock),value:Number(row.value)})).sort((a,b) => a.time-b.time);
    const nearest = points.reduce((best,row) => !best || Math.abs(row.time-target) < Math.abs(best.time-target) ? row : best,null);
    const live = valid(station.temperatura) && nearest && Math.abs(nearest.time-target) <= 15*60000 ? {delta:Number(station.temperatura)-nearest.value,reference:nearest.value,clock:new Date(nearest.time).toISOString().slice(11,16)} : null;
    // Daily aggregates preserve station-reported extremes, which can occur between archive samples.
    const hours = new Set(points.map(row => Math.floor((row.time-Date.parse(`${day}T00:00:00Z`))/3600000)));
    const maxGap = points.reduce((gap,row,i) => i ? Math.max(gap,row.time-points[i-1].time) : gap,0);
    const complete = hours.size >= 18 && hours.has(0) && hours.has(23) && maxGap <= 3*3600000 && daily?.day === day;
    const extreme = (current,reference) => complete && valid(current) && valid(reference) ? {delta:Number(current)-Number(reference),reference:Number(reference)} : null;
    return {live,max:extreme(station.tmax,daily?.temp_max),min:extreme(station.tmin,daily?.temp_min)};
}
async function load(station, day) {
    const key = `${station.id}:${day}`;
    const old = cache.get(key);
    if (old && Date.now()-old.at < 300000) return old.promise;
    const promise = Promise.all([
        window.WSWeatherApi.getStationArchive(station.id,{range:'custom',start:day,end:day}),
        fetch(`/api/station-temperature-day.php?id=${encodeURIComponent(station.id)}&day=${day}`)
            .then(r => r.ok ? r.json() : null).then(data => data?.ok ? data.day : null).catch(() => null)
    ]);
    cache.set(key,{at:Date.now(),promise});
    if (cache.size>100) cache.delete(cache.keys().next().value);
    try { return await promise; } catch (error) { cache.delete(key); throw error; }
}
export async function hydrateTemperatureTrends(container, rec) {
    const station = rec?.station ?? rec;
    const hero = container?.querySelector('[data-temperature-trends]');
    const clock = romeClock(station?.time_utc ?? station?.last_update ?? station?.updated_at ?? station?.ora ?? station?.data);
    if (!hero || !clock || !station?.id || !window.WSWeatherApi?.getStationArchive) return;
    try {
        const [rows,daily] = await load(station,previousDay(clock.slice(0,10)));
        if (!hero.isConnected) return;
        const comparisons = temperatureComparisons(station,rows,daily);
        for (const key of ['live','max','min']) {
            const holder = hero.querySelector(`[data-temperature-delta="${key}"]`);
            if (!holder) continue;
            const result = comparisons[key];
            holder.classList.remove('is-warmer','is-cooler','is-neutral');
            holder.classList.add(result?.delta > 0.049 ? 'is-warmer' : result?.delta < -0.049 ? 'is-cooler' : 'is-neutral');
            holder.querySelector('strong').textContent = result ? deltaText(result.delta) : '—';
            holder.querySelector('small').textContent = key === 'live' ? 'vs ieri, stessa ora'
                : result ? `Ieri: ${Number(result.reference).toLocaleString('it-IT',{minimumFractionDigits:1,maximumFractionDigits:1})} °C` : 'Ieri: —';
            holder.title = result ? `Ieri${key === 'live' ? ` alle ${result.clock}` : ''}: ${Number(result.reference).toLocaleString('it-IT',{minimumFractionDigits:1,maximumFractionDigits:1})} °C${key !== 'live' ? ' · intera giornata' : ''}` : 'Confronto non disponibile: storico assente, incompleto o misura non recente';
        }
    } catch (_) {
        if (hero.isConnected) hero.querySelectorAll('[data-temperature-delta]').forEach(holder => {holder.querySelector('strong').textContent='—';holder.title='Confronto temporaneamente non disponibile';});
    }
}
